import { type Request } from '@luxuryescapes/contract-svc-tour';

import { type MarketingVerticalId, SVC_OFFER_HOTEL_TYPES } from '~/components/Marketing/Tags/verticalConfig';

import type { TagType } from '~/consts/tags';
import { TAG_TYPE_CAMPAIGN } from '~/consts/tags';

import BedbankService from '~/services/BedbankService';
import {
  type CurationParams,
  type ExperienceOffer,
  curationExperience,
  getExperienceOfferById,
} from '~/services/ExperiencesService';
import OffersService from '~/services/OffersService';
import { basePath as toursBasePath } from '~/services/ToursService';
import { json_headers, request } from '~/services/common';
import cruiseOfferService from '~/services/cruises/OfferService';

import ApiError from '~/types/ApiError';

import type { BulkActionMode, BulkActionResult, BulkTagField, HotelBulkTagTarget } from './bulkTagTypes';

const DEFAULT_EXPERIENCE_BRAND = 'luxuryescapes';

type TagMutationContext = {
  verticalId: MarketingVerticalId;
  tagType: TagType;
  mode: BulkActionMode;
  selectedTagNames: Array<string>;
  offerIds: Array<string>;
  catalogTagNames: Array<string>;
  hotelTarget?: HotelBulkTagTarget;
};

type TourRootFormData = Request.TourFormData;

type BedbankPropertyMatch = {
  propertyId: string;
  property: Record<string, unknown> & { updatedAt: string };
};

function getSvcOfferTagField(tagType: TagType): BulkTagField {
  return tagType === TAG_TYPE_CAMPAIGN ? 'campaigns' : 'holiday_types';
}

function getBedbankTagField(tagType: TagType): BulkTagField {
  return tagType === TAG_TYPE_CAMPAIGN ? 'campaigns' : 'holidayTypes';
}

function getCurrentTags(offer: Record<string, unknown>, fieldName: BulkTagField): Array<string> {
  const value = offer[fieldName];
  return Array.isArray(value) ? (value as Array<string>) : [];
}

function buildResult(
  mode: BulkActionMode,
  successes: BulkActionResult['success'],
  unchanged: Array<string>,
  failures: BulkActionResult['failures'],
): BulkActionResult {
  return { mode, success: successes, unchanged, failures };
}

function formatMutationError(error: unknown): string {
  if (error instanceof ApiError) {
    const serialized = JSON.stringify(error.errors ?? error.responseBody ?? '');

    if (serialized.includes('enum values') || serialized.includes('is not one of')) {
      return 'Campaign tag validation failed. Select tag names from the dropdown (not offer IDs). The offer may also have legacy invalid tags — open the offer in admin and fix its campaign tags, then retry.';
    }

    if (error.errors) {
      return `${error.message}: ${serialized}`;
    }

    return error.message;
  }

  return error instanceof Error ? error.message : 'Unknown error occurred.';
}

function isTagEnumValidationError(error: unknown): boolean {
  if (!(error instanceof ApiError)) {
    return false;
  }

  const serialized = JSON.stringify(error.errors ?? error.responseBody ?? '');
  return serialized.includes('enum values') || serialized.includes('is not one of');
}

function isNotFoundError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

function sanitizeTagsToCatalog(tags: Array<string>, catalogTagNames: Set<string>): Array<string> {
  return tags.filter((tag) => catalogTagNames.has(tag));
}

async function patchSvcOfferHotelTags({
  offer,
  offerId,
  field,
  currentTags,
  catalogTagNames,
  selectedTagNames,
  mode,
}: {
  offer: App.Offer;
  offerId: string;
  field: BulkTagField;
  currentTags: Array<string>;
  catalogTagNames: Set<string>;
  selectedTagNames: Array<string>;
  mode: BulkActionMode;
}): Promise<{ strippedTags: Array<string> }> {
  let mutation = computeTagMutation(currentTags, selectedTagNames, mode);

  if (mutation.status === 'unchanged') {
    return { strippedTags: [] };
  }

  try {
    await OffersService.updateOffer({ type: offer.type, [field]: mutation.updatedTags }, offerId);
    return { strippedTags: [] };
  } catch (error) {
    if (!isTagEnumValidationError(error)) {
      throw error;
    }

    const sanitizedCurrent = sanitizeTagsToCatalog(currentTags, catalogTagNames);
    const strippedTags = currentTags.filter((tag) => !catalogTagNames.has(tag));

    if (!strippedTags.length) {
      throw error;
    }

    mutation = computeTagMutation(sanitizedCurrent, selectedTagNames, mode);

    if (mutation.status === 'unchanged') {
      throw error;
    }

    await OffersService.updateOffer({ type: offer.type, [field]: mutation.updatedTags }, offerId);
    return { strippedTags };
  }
}

async function patchBedbankPropertyTags({
  offerId,
  property,
  field,
  currentTags,
  catalogTagNames,
  selectedTagNames,
  mode,
}: {
  offerId: string;
  property: Record<string, unknown> & { updatedAt: string };
  field: BulkTagField;
  currentTags: Array<string>;
  catalogTagNames: Set<string>;
  selectedTagNames: Array<string>;
  mode: BulkActionMode;
}): Promise<{ strippedTags: Array<string> }> {
  let mutation = computeTagMutation(currentTags, selectedTagNames, mode);

  if (mutation.status === 'unchanged') {
    return { strippedTags: [] };
  }

  const patchPayload = {
    [field]: mutation.updatedTags,
    updatedAt: property.updatedAt,
  };

  try {
    await BedbankService.patchPropertyEdit(offerId, patchPayload);
    return { strippedTags: [] };
  } catch (error) {
    if (!isTagEnumValidationError(error)) {
      throw error;
    }

    const sanitizedCurrent = sanitizeTagsToCatalog(currentTags, catalogTagNames);
    const strippedTags = currentTags.filter((tag) => !catalogTagNames.has(tag));

    if (!strippedTags.length) {
      throw error;
    }

    mutation = computeTagMutation(sanitizedCurrent, selectedTagNames, mode);

    if (mutation.status === 'unchanged') {
      throw error;
    }

    await BedbankService.patchPropertyEdit(offerId, {
      [field]: mutation.updatedTags,
      updatedAt: property.updatedAt,
    });

    return { strippedTags };
  }
}

async function tryGetSvcOfferHotel(offerId: string): Promise<App.Offer | null> {
  try {
    // Use the underlying svc-offer endpoint directly. OffersService.getOffer()
    // normalizes package data and swallows request failures, but this bulk tag
    // probe only needs curation fields and should only fall back to Bedbank on a
    // genuine "offer not found".
    const offerResponse = await request(`${OffersService.basePath()}/${offerId}`, {
      method: 'GET',
    });
    const offer = offerResponse?.result as App.Offer | undefined;
    if (!offer || !SVC_OFFER_HOTEL_TYPES.includes(offer.type)) {
      return null;
    }
    return offer;
  } catch (error: unknown) {
    if (!isNotFoundError(error)) {
      throw error;
    }
    return null;
  }
}

function getBedbankLookupId(offerId: string): string {
  return offerId.split('#')[0];
}

async function tryGetBedbankPropertyEdit(propertyId: string): Promise<BedbankPropertyMatch | null> {
  try {
    const propertyResponse = await BedbankService.getPropertyEdit(propertyId);
    const property = propertyResponse?.result;
    return property?.updatedAt ? { propertyId, property } : null;
  } catch {
    return null;
  }
}

async function tryGetBedbankProperty(propertyId: string): Promise<BedbankPropertyMatch | null> {
  const lookupId = getBedbankLookupId(propertyId);
  const directProperty = await tryGetBedbankPropertyEdit(lookupId);

  if (directProperty) {
    return directProperty;
  }

  try {
    const suppliersResponse = await BedbankService.getSuppliersProperties({
      page: 1,
      limit: 1,
      sortBy: 'hotelName',
      orderBy: 'asc',
      filterById: lookupId,
      filterByMapping: 'true',
    });
    const supplierProperty = suppliersResponse?.result?.[0];
    const masterPropertyId = supplierProperty?.masterProperties?.find(
      (masterProperty) => masterProperty.state === 'mapped',
    )?.id;

    if (masterPropertyId) {
      const masterProperty = await tryGetBedbankPropertyEdit(masterPropertyId);
      if (masterProperty) {
        return masterProperty;
      }
    }
  } catch {
    // Fall through to preview lookup.
  }

  try {
    const previewResponse = await BedbankService.getPropertyById(lookupId);
    const property = previewResponse?.result as (Record<string, unknown> & { updatedAt?: string }) | undefined;

    if (property?.updatedAt) {
      return {
        propertyId: lookupId,
        property: property as Record<string, unknown> & { updatedAt: string },
      };
    }

    const listResponse = await BedbankService.getProperties(1, 1, {
      query: lookupId,
      type: 'id',
    });
    const listProperty = (
      listResponse?.result as Array<Record<string, unknown> & { updatedAt?: string }> | undefined
    )?.find((result) => result.id === lookupId);

    if (property && listProperty?.updatedAt) {
      return {
        propertyId: lookupId,
        property: {
          ...property,
          updatedAt: listProperty.updatedAt,
        },
      };
    }
  } catch {
    return null;
  }

  return null;
}

async function mutateHotelTags({
  tagType,
  mode,
  selectedTagNames,
  offerIds,
  catalogTagNames,
  hotelTarget = 'svc-offer',
}: TagMutationContext): Promise<BulkActionResult> {
  const svcOfferField = getSvcOfferTagField(tagType);
  const bedbankField = getBedbankTagField(tagType);
  const catalogTagNameSet = new Set(catalogTagNames);

  const successes: BulkActionResult['success'] = [];
  const unchanged: Array<string> = [];
  const failures: BulkActionResult['failures'] = [];

  for (const offerId of offerIds) {
    try {
      if (hotelTarget === 'svc-offer') {
        const offer = await tryGetSvcOfferHotel(offerId);
        if (!offer) {
          failures.push({
            offerId,
            reason: 'Flash/LPC hotel offer not found.',
          });
          continue;
        }

        const currentTags = getCurrentTags(offer as unknown as Record<string, unknown>, svcOfferField);
        const mutation = computeTagMutation(currentTags, selectedTagNames, mode);

        if (mutation.status === 'unchanged') {
          unchanged.push(offerId);
          continue;
        }

        const { strippedTags } = await patchSvcOfferHotelTags({
          offer,
          offerId,
          field: svcOfferField,
          currentTags,
          catalogTagNames: catalogTagNameSet,
          selectedTagNames,
          mode,
        });

        successes.push({
          offerId,
          changedTags: mutation.changedTags,
          skippedTags: mutation.skippedTags,
          ...(strippedTags.length ? { strippedTags } : {}),
        });
        continue;
      }

      const bedbankMatch = await tryGetBedbankProperty(offerId);

      if (!bedbankMatch) {
        failures.push({
          offerId,
          reason: 'Bedbank property not found.',
        });
        continue;
      }

      const { propertyId: bedbankPropertyId, property } = bedbankMatch;
      const currentTags = getCurrentTags(property as unknown as Record<string, unknown>, bedbankField);
      const mutation = computeTagMutation(currentTags, selectedTagNames, mode);

      if (mutation.status === 'unchanged') {
        unchanged.push(offerId);
        continue;
      }

      const { strippedTags } = await patchBedbankPropertyTags({
        offerId: bedbankPropertyId,
        property,
        field: bedbankField,
        currentTags,
        catalogTagNames: catalogTagNameSet,
        selectedTagNames,
        mode,
      });

      successes.push({
        offerId,
        changedTags: mutation.changedTags,
        skippedTags: mutation.skippedTags,
        ...(strippedTags.length ? { strippedTags } : {}),
      });
    } catch (error: unknown) {
      failures.push({
        offerId,
        reason: formatMutationError(error),
      });
    }
  }

  return buildResult(mode, successes, unchanged, failures);
}

async function mutateCruiseTags({ mode, selectedTagNames, offerIds }: TagMutationContext): Promise<BulkActionResult> {
  const successes: BulkActionResult['success'] = [];
  const unchanged: Array<string> = [];
  const failures: BulkActionResult['failures'] = [];

  for (const offerId of offerIds) {
    try {
      // Invalid cruises (no departures/prices) 404 without allowInvalidOffer — ops still need to tag them.
      const offerResponse = await cruiseOfferService.getById(offerId, { allowInvalidOffer: true });
      const offer = offerResponse?.result;

      if (!offer) {
        failures.push({
          offerId,
          reason:
            'Cruise offer not found. Use the cruise offer UUID from Cruises admin (not a hotel or search document ID).',
        });
        continue;
      }

      if (!offer.id || !offer.status) {
        failures.push({
          offerId,
          reason: 'Cruise offer is missing required curation fields.',
        });
        continue;
      }

      const currentTags = getCurrentTags(offer, 'campaigns');
      const mutation = computeTagMutation(currentTags, selectedTagNames, mode);

      if (mutation.status === 'unchanged') {
        unchanged.push(offerId);
        continue;
      }

      // Matches single-offer curation save in CruiseOfferDetails.
      await cruiseOfferService.setCurationById(offer.id, {
        leadDepartureId: offer.leadDepartureId ?? null,
        slug: offer.slug,
        status: offer.status,
        leExclusive: offer.leExclusive,
        luxPremiumCollection: offer.luxPremiumCollection,
        disableDeposits: offer.disableDeposits,
        showPricePerNight: offer.showPricePerNight,
        editableSailingName: offer.displayName ?? offer.name,
        heroImageId: offer.heroImageId,
        editableSailingDescription: offer.leVisibleDescription,
        campaigns: mutation.updatedTags,
      });

      successes.push({
        offerId,
        changedTags: mutation.changedTags,
        skippedTags: mutation.skippedTags,
      });
    } catch (error: unknown) {
      const reason =
        error instanceof ApiError && error.status === 404
          ? 'Cruise offer not found. Use the cruise offer UUID from Cruises admin (not a hotel or search document ID).'
          : formatMutationError(error);
      failures.push({
        offerId,
        reason,
      });
    }
  }

  return buildResult(mode, successes, unchanged, failures);
}

function buildExperienceCurationPayload(offer: ExperienceOffer, campaigns: Array<string>): CurationParams {
  // Matches ExperiencesOverview createExperience: send the full curation payload from GET.
  const source = offer as ExperienceOffer & Partial<CurationParams>;

  return {
    curationStatus: source.curationStatus,
    isFlash: source.isFlash,
    title: source.title,
    location: source.location as CurationParams['location'],
    categories: source.categories,
    heroImageId: source.heroImageId,
    campaigns,
    showPostPurchase: source.showPostPurchase,
    searchEngineMarketingEnabled: source.searchEngineMarketingEnabled,
    unlisted: source.unlisted,
    dealDescription: source.dealDescription,
    shortDescription: source.shortDescription,
    included: source.included,
    thingsToKnowNew: source.thingsToKnowNew,
    tickets: source.tickets,
    images: source.images,
    hideTimeSlots: source.hideTimeSlots,
    isNonRefundable: source.isNonRefundable,
    video: source.video,
    excludedRegions: source.excludedRegions as CurationParams['excludedRegions'],
    bookingOptions: source.bookingOptions,
    vendorBookingCutoff: source.vendorBookingCutoff,
    features: source.features,
    ticketUnitLabel: source.ticketUnitLabel,
    baseSalesPrice: source.baseSalesPrice,
    allowBuyNowBookLater: source.allowBuyNowBookLater,
    allowGiftCard: source.allowGiftCard,
    idSalesforceExternal: source.idSalesforceExternal,
    brands: source.brands,
    cancellationInfo: source.cancellationInfo,
    showInJourneys: source.showInJourneys,
    bookingFields: source.bookingFields,
    margin: source.margin,
    commissionMargin: source.commissionMargin,
    bundleMinMarkupPct: source.bundleMinMarkupPct,
    ticketed: source.ticketed,
    targetAudiences: source.targetAudiences,
    disableReviews: source.disableReviews,
    curationComment: source.curationComment,
    memberDiscountEnabled: source.memberDiscountEnabled,
    memberDiscountOverrides: source.memberDiscountOverrides,
    brandSchedules: source.brandSchedules,
  };
}

async function mutateExperienceTags({
  mode,
  selectedTagNames,
  offerIds,
}: TagMutationContext): Promise<BulkActionResult> {
  const successes: BulkActionResult['success'] = [];
  const unchanged: Array<string> = [];
  const failures: BulkActionResult['failures'] = [];

  for (const offerId of offerIds) {
    try {
      const offerResponse = await getExperienceOfferById({
        id: offerId,
        brand: DEFAULT_EXPERIENCE_BRAND,
        curationData: true,
      });
      const offer = offerResponse?.result;

      if (!offer) {
        failures.push({ offerId, reason: 'Experience offer not found.' });
        continue;
      }

      if (!offer.curationStatus || !offer.title) {
        failures.push({ offerId, reason: 'Experience offer is missing required curation fields.' });
        continue;
      }

      const currentTags = getCurrentTags(offer, 'campaigns');
      const mutation = computeTagMutation(currentTags, selectedTagNames, mode);

      if (mutation.status === 'unchanged') {
        unchanged.push(offerId);
        continue;
      }

      await curationExperience(
        offerId,
        DEFAULT_EXPERIENCE_BRAND,
        buildExperienceCurationPayload(offer, mutation.updatedTags),
      );

      successes.push({
        offerId,
        changedTags: mutation.changedTags,
        skippedTags: mutation.skippedTags,
      });
    } catch (error: unknown) {
      failures.push({
        offerId,
        reason: formatMutationError(error),
      });
    }
  }

  return buildResult(mode, successes, unchanged, failures);
}

function getTourRootFormUrl(tourId: string): string {
  return `${toursBasePath()}/forms/${tourId}`;
}

function getTourCampaignTagsFromFormData(formData: TourRootFormData): Array<string> {
  const campaignTags = formData.tour?.campaignTags;
  return Array.isArray(campaignTags) ? campaignTags : [];
}

async function fetchTourRootForm(tourId: string): Promise<TourRootFormData> {
  const response = await request(getTourRootFormUrl(tourId), {
    method: 'GET',
  });
  return response.result.formData as TourRootFormData;
}

async function patchTourRootForm(tourId: string, formData: TourRootFormData): Promise<void> {
  await request(getTourRootFormUrl(tourId), {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(formData),
  });
}

async function patchTourCampaignTags(
  tourId: string,
  campaignTags: Array<string>,
  formData: TourRootFormData,
): Promise<void> {
  await patchTourRootForm(tourId, {
    ...formData,
    tour: {
      ...formData.tour,
      campaignTags,
    },
  });
}

async function mutateTourTags({ mode, selectedTagNames, offerIds }: TagMutationContext): Promise<BulkActionResult> {
  const successes: BulkActionResult['success'] = [];
  const unchanged: Array<string> = [];
  const failures: BulkActionResult['failures'] = [];

  for (const offerId of offerIds) {
    try {
      const formData = await fetchTourRootForm(offerId);
      const currentTags = getTourCampaignTagsFromFormData(formData);
      const mutation = computeTagMutation(currentTags, selectedTagNames, mode);

      if (mutation.status === 'unchanged') {
        unchanged.push(offerId);
        continue;
      }

      // Matches TourRootDetailsForm: campaignTags live on the root tour form (tour.campaignTags).
      await patchTourCampaignTags(offerId, mutation.updatedTags, formData);

      successes.push({
        offerId,
        changedTags: mutation.changedTags,
        skippedTags: mutation.skippedTags,
      });
    } catch (error: unknown) {
      failures.push({
        offerId,
        reason: formatMutationError(error),
      });
    }
  }

  return buildResult(mode, successes, unchanged, failures);
}

function computeTagMutation(currentTags: Array<string>, selectedTagNames: Array<string>, mode: BulkActionMode) {
  const presentTags = selectedTagNames.filter((tagName) => currentTags.includes(tagName));
  const missingTags = selectedTagNames.filter((tagName) => !currentTags.includes(tagName));

  if (mode === 'apply') {
    if (!missingTags.length) {
      return { status: 'unchanged' as const };
    }

    return {
      status: 'changed' as const,
      updatedTags: [...currentTags, ...missingTags],
      changedTags: missingTags,
      skippedTags: presentTags,
    };
  }

  if (!presentTags.length) {
    return { status: 'unchanged' as const };
  }

  return {
    status: 'changed' as const,
    updatedTags: currentTags.filter((tag) => !selectedTagNames.includes(tag)),
    changedTags: presentTags,
    skippedTags: missingTags,
  };
}

export async function runBulkTagMutation(context: TagMutationContext): Promise<BulkActionResult> {
  switch (context.verticalId) {
    case 'hotels':
      return mutateHotelTags(context);
    case 'cruises':
      return mutateCruiseTags(context);
    case 'experiences':
      return mutateExperienceTags(context);
    case 'tours':
      return mutateTourTags(context);
    default: {
      const unsupportedVertical: never = context.verticalId;
      throw new Error(`Unsupported vertical: ${unsupportedVertical}`);
    }
  }
}
